import { makeAutoObservable, runInAction } from 'mobx'

import { diskViewerMode } from '../Helpers/diskViewerMode'
import { CountDiskFolderItemsTask } from '../Tasks/CountDiskFolderItemsTask'
import { GetDiskContentUrlTask } from '../Tasks/GetDiskContentUrlTask'
import { LoadDiskNodeSharesTask } from '../Tasks/LoadDiskNodeSharesTask'
import { DiskNodeInfoStore } from './DiskNodeInfoStore'

import type { DiskNode } from '../Contracts/DiskNode'
import type { DiskNodeShares } from '../Contracts/DiskShare'

/**
 * Дровер «Властивості» екрана диска (54631): бічна панель справа поверх
 * списку, як у референсі (`FilePreviewDrawer`) і Google Drive. Файл при цьому
 * не відкривається — переглядач лише подвійним кліком чи «Відкрити».
 *
 * - Картка й журнал — той самий `DiskNodeInfoStore`, що й в інфопанелі
 *   переглядача; рядок списку видно одразу, поки йде картка.
 * - Папка — скільки в ній елементів: `total` списку папки з `limit=1`
 *   (сумарного розміру бек дешево не дає — його немає).
 * - Зображення — мініатюра за підписаним `inline`-посиланням.
 * - Вибір іншого рядка при відкритому дровері — `follow()` (екран диска).
 * - «Доступ» — хто має доступ (`GET …/shares`), коротко; керують у модалці
 *   «Поділитися», після змін — `reloadAccess()`.
 */
export class DiskPropertiesStore {
    readonly info = new DiskNodeInfoStore()
    /** Скільки елементів у папці (безпосередньо); `null` — не папка або ще не відомо. */
    folderItems: number | null = null
    /** Мініатюра зображення; `null` — не зображення, ще вантажиться чи не вдалося. */
    thumbUrl: string | null = null
    /** «Хто має доступ»; `null` — ще вантажиться чи не вдалося (`accessError`). */
    access: DiskNodeShares | null = null
    accessError: string | null = null

    private requestId = 0

    constructor() {
        makeAutoObservable<DiskPropertiesStore, 'requestId'>(this, { info: false, requestId: false })
    }

    get isOpen(): boolean {
        return this.info.isOpen
    }

    get uuid(): string | null {
        return this.info.uuid
    }

    /** Показати властивості вузла (рядок списку — одразу, картка — слідом). */
    open(node: DiskNode, force = false): void {
        if (!force && this.info.uuid === node.uuid) return

        const id = ++this.requestId
        const sameNode = this.info.uuid === node.uuid

        if (!sameNode) {
            this.folderItems = null
            this.thumbUrl = null
            this.access = null
        }

        void this.info.open(node.uuid, node, force)
        void this.loadAccess(node.uuid, id)

        if (node.type === 'folder') void this.loadFolderItems(node.uuid, id)
        else if (diskViewerMode(node) === 'image' && node.permissions.read === true) void this.loadThumb(node.uuid, id)
    }

    /**
     * Дровер відкритий, а людина вибрала інший рядок — показати його. Той
     * самий рядок, що змінився (перейменування, нова версія), — перечитати.
     */
    follow(node: DiskNode): void {
        if (!this.isOpen) return

        const shown = this.info.node

        if (this.info.uuid !== node.uuid) {
            this.open(node)
        } else if (shown !== null && !this.info.isLoading && (shown.name !== node.name || shown.updatedAt !== node.updatedAt)) {
            this.open(node, true)
        }
    }

    close(): void {
        this.requestId += 1
        this.info.close()
        this.folderItems = null
        this.thumbUrl = null
        this.access = null
        this.accessError = null
    }

    /** Після змін у модалці «Поділитися» — перечитати «Доступ» показаного вузла. */
    reloadAccess(): void {
        const uuid = this.info.uuid
        if (uuid !== null) void this.loadAccess(uuid, this.requestId)
    }

    private async loadAccess(uuid: string, id: number): Promise<void> {
        this.accessError = null

        try {
            const access = await LoadDiskNodeSharesTask(uuid)

            runInAction(() => {
                if (id === this.requestId) this.access = access
            })
        } catch {
            runInAction(() => {
                if (id === this.requestId) this.accessError = 'Не вдалося завантажити, хто має доступ'
            })
        }
    }

    private async loadFolderItems(uuid: string, id: number): Promise<void> {
        try {
            const total = await CountDiskFolderItemsTask(uuid)

            runInAction(() => {
                if (id === this.requestId) this.folderItems = total
            })
        } catch {
            // Кількість — додаткове: без неї решта властивостей лишається
        }
    }

    private async loadThumb(uuid: string, id: number): Promise<void> {
        try {
            // Мініатюра — не відкриття: у «Нещодавні» не потрапляє
            const { url } = await GetDiskContentUrlTask(uuid, 'inline', { recent: false })

            runInAction(() => {
                if (id === this.requestId) this.thumbUrl = url
            })
        } catch {
            // Без мініатюри — іконка виду
        }
    }

    /** Мініатюра не відкрилась (посилання прострочене, файл зіпсований) — іконка. */
    thumbFailed(): void {
        this.thumbUrl = null
    }
}

export const diskPropertiesStore = new DiskPropertiesStore()
